import React, { useState } from 'react';
import {
  IDataRefreshConfigInput,
  IDataRefreshConfigMetaFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { DataModelerConfigLabelWithInfo } from '../DataModelerShared';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import { InputField, VerticalField } from '../../../../components/Form';
import { z } from 'zod';
import { useAppDispatch } from '../../../../redux/hooks';
import { dataModelerUpdateDataRefreshConfig } from '../../../../redux/slices/dataModeler';
import { isNil } from 'lodash';
import WarningModal from '../../../../components/ConfirmationModal/WarningModal';

interface IProps {
  isEditMode: boolean;
  dataRefreshInput: IDataRefreshConfigInput | null;
  dataRefreshMeta: IDataRefreshConfigMetaFieldsFragment;
}

const validPositiveInteger = z.coerce.number().nonnegative().int();
const WARNING_MRI_SECONDS_THRESHOLD = 3600;

const SchemaDataRefreshConfig = ({
  isEditMode,
  dataRefreshInput,
  dataRefreshMeta,
}: IProps) => {
  const dispatch = useAppDispatch();
  const minimumRefreshSeconds =
    dataRefreshInput?.minimumRefreshSeconds ??
    dataRefreshMeta.minimumRefreshSeconds;
  const [isConfirmTooLowMriModalOpen, setIsConfirmTooLowMriModalOpen] =
    useState(false);
  const [isConfirmTooLowMriAccepted, setIsConfirmTooLowMriAccepted] =
    useState(false);

  const setMriHoursStr = (s: string) => {
    const newSeconds = validPositiveInteger.safeParse(s).data ?? null;
    // Pop-up the confirmation dialog if the user set the value to 0
    if (
      !isNil(newSeconds) &&
      !isConfirmTooLowMriAccepted &&
      newSeconds < WARNING_MRI_SECONDS_THRESHOLD
    ) {
      setIsConfirmTooLowMriModalOpen(true);
    }
    dispatch(
      dataModelerUpdateDataRefreshConfig({
        minimumRefreshSeconds: newSeconds,
      }),
    );
  };

  // The user accepted the 0-value - just dismiss the modal.
  const handleConfirma0Mri = () => {
    setIsConfirmTooLowMriAccepted(true);
    setIsConfirmTooLowMriModalOpen(false);
  };

  // The user answered No to the warning - reset the value to non-0 (1)
  const handleCancelConfirma0Mri = () => {
    dispatch(
      dataModelerUpdateDataRefreshConfig({
        minimumRefreshSeconds: WARNING_MRI_SECONDS_THRESHOLD,
      }),
    );
    setIsConfirmTooLowMriModalOpen(false);
  };

  const maximumValueLength =
    dataRefreshInput?.pfvStringMaxLength ?? dataRefreshMeta.pfvStringMaxLength;
  const setMaximumValueLengthStr = (s: string) => {
    const newMaxVal = validPositiveInteger.safeParse(s).data ?? null;
    dispatch(
      dataModelerUpdateDataRefreshConfig({
        pfvStringMaxLength: newMaxVal,
      }),
    );
  };

  const lookbackSeconds =
    dataRefreshInput?.lookBackSeconds ?? dataRefreshMeta.lookBackSeconds;
  const setLookbackSecondsStr = (s: string) => {
    const newVal = validPositiveInteger.safeParse(s).data ?? null;
    dispatch(
      dataModelerUpdateDataRefreshConfig({
        lookBackSeconds: newVal,
      }),
    );
  };

  return (
    <>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Minimum Refresh Interval Seconds (MRI)'}
          tooltipOption={
            dataModelLabelInfoTooltipOptions.minimumRefreshInterval
          }
        />
        <InputField
          type="number"
          min={0}
          value={minimumRefreshSeconds}
          onChange={setMriHoursStr}
          disabled={!isEditMode}
          placeholder={'0'}
        />
        {minimumRefreshSeconds < WARNING_MRI_SECONDS_THRESHOLD &&
          !isConfirmTooLowMriModalOpen && (
            <div>
              Proceeding with a refresh interval shorter than 1 Hour. Please
              monitor your compute costs regularly to avoid unexpected charges.
            </div>
          )}
      </VerticalField>
      {isConfirmTooLowMriModalOpen && (
        <WarningModal
          onConfirm={handleConfirma0Mri}
          onCancel={handleCancelConfirma0Mri}
          trackContext="data modeler"
          trackConfirmAction="refresh interval - confirm"
          trackCancelAction="refresh interval - cancel"
        >
          Setting a refresh interval shorter than 1 hour could significantly
          increase your warehouse compute costs. Are you sure you want to
          continue?
        </WarningModal>
      )}
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Maximum Value Length'}
          tooltipOption={dataModelLabelInfoTooltipOptions.maximumValueLength}
        />
        <InputField
          type="number"
          min={0}
          value={maximumValueLength ?? ''}
          onChange={setMaximumValueLengthStr}
          disabled={!isEditMode}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Look Back Seconds'}
          tooltipOption={dataModelLabelInfoTooltipOptions.lookBackSeconds}
        />
        <InputField
          type="number"
          min={0}
          value={lookbackSeconds ?? ''}
          onChange={setLookbackSecondsStr}
          disabled={!isEditMode}
        />
      </VerticalField>
    </>
  );
};

export default SchemaDataRefreshConfig;
